import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage, Language } from '../../context/LanguageContext';
import { ReactComponent as FlagEN } from '../../assets/icons/flag-en.svg';
import { ReactComponent as FlagDE } from '../../assets/icons/flag-de.svg';
import { ReactComponent as FlagFR } from '../../assets/icons/flag-fr.svg';
import { ReactComponent as FlagZH } from '../../assets/icons/flag-zh.svg';
// 🌐 Language options with their respective flags
const LANGUAGE_OPTIONS = [
  { value: 'en', Flag: FlagEN, label: 'English' },
  { value: 'de', Flag: FlagDE, label: 'Deutsch' },
  { value: 'fr', Flag: FlagFR, label: 'Français' },
  { value: 'zh', Flag: FlagZH, label: '中文' },
] as const;

function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown position based on button position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Calculate initial position
        let top = rect.bottom;
        let left = rect.left;

        // Check if dropdown would go below viewport and adjust if needed
        const dropdownHeight = dropdownRef.current?.offsetHeight || 150; // Estimated height if not yet rendered
        if (top + dropdownHeight > viewportHeight) {
          top = rect.top - dropdownHeight; // Position above button
        }

        // Check if dropdown would go beyond right edge and adjust if needed
        const dropdownWidth = dropdownRef.current?.offsetWidth || 150; // Estimated width
        if (left + dropdownWidth > viewportWidth) {
          left = viewportWidth - dropdownWidth - 10; // 10px padding from edge
        }

        // Ensure dropdown doesn't go beyond left edge
        left = Math.max(10, left);

        setDropdownPosition({ top, left });
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    setIsOpen(false);
  };

  // Find the current language option
  const currentOption = LANGUAGE_OPTIONS.find(opt => opt.value === language);
  const CurrentFlag = currentOption?.Flag;

  // Dropdown portal component
  const DropdownPortal = () => {
    const portalDiv = document.getElementById('portal-root') || document.body;

    return createPortal(
      <div
        ref={dropdownRef}
        className="fixed bg-white rounded-md shadow-lg py-1 z-[10000]"
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
        }}
      >
        {LANGUAGE_OPTIONS.map(({ value, Flag, label }) => (
          <button
            key={value}
            onClick={() => handleLanguageChange(value as Language)}
            className={`w-full flex items-center px-4 py-2 hover:bg-gray-100 transition-colors
              ${value === language ? 'bg-gray-50' : ''}`}
            aria-label={`Switch to ${label}`}
          >
            <Flag className="w-6 h-4 mr-2" />
          </button>
        ))}
      </div>,
      portalDiv
    );
  };

  return (
    <div className="inline-block">
      {/* Current flag button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 p-2 rounded-md hover:bg-gray-100 transition-colors"
        aria-label="Select language"
      >
        {CurrentFlag && <CurrentFlag className="w-6 h-4" />}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </button>

      {/* Render dropdown through portal */}
      {isOpen && <DropdownPortal />}
    </div>
  );
}

export default LanguageSelector;
